'use client'

import { cn } from '@brew/ui/cn'
import { FileCode2, Mail, Sparkles, Upload } from 'lucide-react'
import {
  type Dispatch,
  type FormEvent,
  type KeyboardEvent,
  useCallback,
  useId,
} from 'react'
import { homeButtonClasses } from '@/components/marketing/home/home-button'
import {
  MK_CAPTION,
  MK_CARD,
  MK_SMALL,
} from '@/components/marketing/home/home-layout'
import {
  FREE_EMAIL_AUDIT_MAX_HTML_BYTES,
  utf8ByteLength,
} from '@/lib/free-tools/email-audit/contracts'
import {
  EMAIL_AUDIT_TAB_IDS,
  type EmailAuditTabId,
} from '@/lib/site/email-audit-content'
import { useFormatAuditBytes } from './email-audit-format'
import {
  ByEmailPanel,
  EnvelopeFields,
  PastePanel,
  UploadPanel,
} from './email-audit-input-panels'
import { EmailAuditProgress } from './email-audit-progress'
import type {
  EmailAuditToolAction,
  EmailAuditToolState,
} from './email-audit-tool-state'
import { useEmailAuditCopy } from './use-email-audit-copy'

const TAB_ICON = { paste: FileCode2, upload: Upload, email: Mail } as const

type InputCardProps = {
  state: EmailAuditToolState
  dispatch: Dispatch<EmailAuditToolAction>
  onSubmit: (event: FormEvent<HTMLFormElement>) => void
  onSample: () => void
  isSampleLoading: boolean
}

/**
 * The free audit's input: paste, upload or send by email, then one button.
 * While the audit runs the card shows its progress in place, so the page
 * never jumps.
 */
export function EmailAuditInputCard({
  state,
  dispatch,
  onSubmit,
  onSample,
  isSampleLoading,
}: InputCardProps) {
  const baseId = useId()
  const copy = useEmailAuditCopy()
  const formatBytes = useFormatAuditBytes()
  const isRunning = state.status.kind === 'running'
  const tabId = (tab: EmailAuditTabId) => `${baseId}-tab-${tab}`
  const panelId = `${baseId}-panel`

  const handleTabKey = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      const step = { ArrowRight: 1, ArrowLeft: -1 }[event.key]
      if (!step) {
        return
      }
      event.preventDefault()
      const index = EMAIL_AUDIT_TAB_IDS.indexOf(state.tab)
      const next =
        EMAIL_AUDIT_TAB_IDS[
          (index + step + EMAIL_AUDIT_TAB_IDS.length) %
            EMAIL_AUDIT_TAB_IDS.length
        ]
      if (next) {
        dispatch({ type: 'tab', tab: next })
        document.getElementById(`${baseId}-tab-${next}`)?.focus()
      }
    },
    [baseId, dispatch, state.tab]
  )

  return (
    <form
      onSubmit={onSubmit}
      aria-busy={isRunning}
      className={cn(
        MK_CARD,
        'flex w-full max-w-3xl flex-col overflow-clip text-left'
      )}
    >
      <div className="flex flex-col items-stretch justify-between gap-1 border-mk-card-line border-b p-2 sm:flex-row sm:items-center sm:gap-3 sm:p-3">
        <div
          role="tablist"
          aria-label={copy.tabsLabel}
          onKeyDown={handleTabKey}
          className="flex w-full min-w-0 gap-1 rounded-full bg-mk-tile p-1 ring-1 ring-mk-line sm:w-auto"
        >
          {copy.tabs.map((tab) => (
            <TabButton
              key={tab.id}
              id={tabId(tab.id)}
              panelId={panelId}
              tab={tab.id}
              label={tab.label}
              isActive={state.tab === tab.id}
              isDisabled={isRunning}
              dispatch={dispatch}
            />
          ))}
        </div>
        <button
          type="button"
          onClick={onSample}
          disabled={isRunning || isSampleLoading}
          className={cn(
            MK_CAPTION,
            'flex shrink-0 items-center gap-1.5 self-end rounded-full px-3 py-1.5 font-medium text-mk-accent-text transition-colors duration-quick ease-smooth-out hover:bg-mk-tile disabled:opacity-50 sm:self-auto'
          )}
        >
          <Sparkles aria-hidden="true" className="size-3.5" />
          <span className="hidden sm:inline">{copy.form.sample}</span>
          <span className="sm:hidden">{copy.form.sampleShort}</span>
        </button>
      </div>

      <div
        id={panelId}
        role="tabpanel"
        aria-labelledby={tabId(state.tab)}
        className="flex flex-col"
      >
        {isRunning ? (
          <EmailAuditProgress className="min-h-72 justify-center px-6" />
        ) : (
          <TabBody state={state} dispatch={dispatch} />
        )}
        {state.inputError ? (
          <p
            role="alert"
            className={cn(
              MK_SMALL,
              'px-5 pb-4 text-red-600 sm:px-6 dark:text-red-400'
            )}
          >
            {state.inputError}
          </p>
        ) : null}
        {state.status.kind === 'error' ? (
          <p
            role="alert"
            className={cn(
              MK_SMALL,
              'px-5 pb-4 text-red-600 sm:px-6 dark:text-red-400'
            )}
          >
            {state.status.message}
          </p>
        ) : null}
      </div>

      {state.tab === 'email' ? null : (
        <div className="flex flex-col-reverse items-stretch gap-3 border-mk-card-line border-t bg-mk-tile/50 px-5 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-6">
          <span className={cn(MK_CAPTION, 'font-mk-mono text-mk-ink-2')}>
            {state.html
              ? copy.form.sizeUsed(
                  formatBytes(utf8ByteLength(state.html)),
                  formatBytes(FREE_EMAIL_AUDIT_MAX_HTML_BYTES)
                )
              : copy.form.sizeHint}
          </span>
          <button
            type="submit"
            disabled={isRunning || state.pendingLoad !== null}
            className={homeButtonClasses({
              className: 'h-11 gap-1 px-6 text-[15px] disabled:opacity-70',
            })}
          >
            {isRunning ? copy.form.submitting : copy.form.submit}
            <span aria-hidden="true">→</span>
          </button>
        </div>
      )}
    </form>
  )
}

function TabButton({
  id,
  panelId,
  tab,
  label,
  isActive,
  isDisabled,
  dispatch,
}: {
  id: string
  panelId: string
  tab: EmailAuditTabId
  label: string
  isActive: boolean
  isDisabled: boolean
  dispatch: Dispatch<EmailAuditToolAction>
}) {
  const Icon = TAB_ICON[tab]
  const handleClick = useCallback(
    () => dispatch({ type: 'tab', tab }),
    [dispatch, tab]
  )
  return (
    <button
      id={id}
      type="button"
      role="tab"
      aria-selected={isActive}
      aria-controls={panelId}
      tabIndex={isActive ? 0 : -1}
      disabled={isDisabled}
      onClick={handleClick}
      className={cn(
        MK_CAPTION,
        'flex min-h-8 min-w-0 flex-1 items-center justify-center gap-1 rounded-full px-2 py-1 font-medium text-mk-ink-2 transition-[color,background-color,box-shadow] duration-quick ease-smooth-out hover:text-mk-ink focus-visible:outline-2 focus-visible:outline-mk-accent disabled:opacity-60 sm:h-8 sm:flex-none sm:justify-start sm:gap-1.5 sm:px-3 sm:py-0',
        { 'bg-mk-card text-mk-ink shadow-mk-card': isActive }
      )}
    >
      <Icon aria-hidden="true" className="size-3.5 shrink-0" />
      <span className="text-center sm:truncate sm:text-left">{label}</span>
    </button>
  )
}

function TabBody({
  state,
  dispatch,
}: {
  state: EmailAuditToolState
  dispatch: Dispatch<EmailAuditToolAction>
}) {
  if (state.tab === 'email') {
    return <ByEmailPanel />
  }
  return (
    <>
      {state.tab === 'paste' ? (
        <PastePanel html={state.html} dispatch={dispatch} />
      ) : (
        <UploadPanel file={state.file} dispatch={dispatch} />
      )}
      <EnvelopeFields
        subject={state.subject}
        previewText={state.previewText}
        dispatch={dispatch}
      />
    </>
  )
}
